import type { PortfolioSnapshot } from '../lib/supabase'

interface Props {
  snapshot: PortfolioSnapshot | null
  periodLabel?: string
  periodPnl?: number
  periodPnlPct?: number
  benchmarkReturnPct?: number
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

function formatPercent(value: number, decimals: number = 2): string {
  return `${value >= 0 ? '+' : ''}${value.toFixed(decimals)}%`
}

export function PortfolioOverview({ snapshot, periodLabel, periodPnl, periodPnlPct, benchmarkReturnPct }: Props) {
  if (!snapshot) {
    return (
      <div className="card">
        <div className="empty-state">
          <div className="empty-state-icon">📊</div>
          <div className="empty-state-text">No portfolio data available</div>
        </div>
      </div>
    )
  }

  const isPeriodView = periodPnl !== undefined && periodPnlPct !== undefined
  const isInsufficientData = periodLabel && periodLabel !== 'Today' && !isPeriodView
  const displayPnl = isPeriodView ? periodPnl! : snapshot.daily_pnl
  const displayPnlPct = isPeriodView ? periodPnlPct! : snapshot.daily_pnl_pct
  const pnlClass = displayPnl >= 0 ? 'positive' : 'negative'
  const pnlLabel = isPeriodView && periodLabel
    ? `${periodLabel} P&L`
    : (snapshot.trading_days_gap ?? 1) > 1 ? `P&L (${snapshot.trading_days_gap}d)` : 'Daily P&L'

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">Portfolio Overview</h2>
        <span className="card-badge">{snapshot.snapshot_date}</span>
      </div>

      <div className="metrics-grid">
        <div className="metric">
          <span className="metric-label">Net Worth</span>
          <span className="metric-value">{formatCurrency(snapshot.net_worth)}</span>
          {isInsufficientData ? (
            <span className="metric-change" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-dim)' }}>
              Insufficient data
            </span>
          ) : (
            <span className={`metric-change ${pnlClass}`}>
              {formatCurrency(displayPnl)} ({formatPercent(displayPnlPct)})
            </span>
          )}
        </div>

        <div className="metric">
          <span className="metric-label">Cash Available</span>
          <span className="metric-value">{formatCurrency(snapshot.cash)}</span>
          <span className="metric-change" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-muted)' }}>
            {((snapshot.cash / snapshot.net_worth) * 100).toFixed(1)}% of portfolio
          </span>
        </div>

        <div className="metric">
          <span className="metric-label">Positions Value</span>
          <span className="metric-value">{formatCurrency(snapshot.positions_value)}</span>
          <span className="metric-change" style={{ background: 'var(--accent-cyan-glow)', color: 'var(--accent-cyan)' }}>
            {snapshot.position_count} positions
          </span>
        </div>

        <div className="metric">
          <span className="metric-label">Total Exposure</span>
          <span className={`metric-value ${snapshot.total_exposure_pct > 100 ? 'warning' : ''}`}>
            {snapshot.total_exposure_pct.toFixed(1)}%
          </span>
          <span className="metric-change" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-muted)' }}>
            Target: 75-125%
          </span>
        </div>

        <div className="metric">
          <span className="metric-label">Drawdown</span>
          <span className={`metric-value ${snapshot.drawdown_pct > 10 ? 'warning' : snapshot.drawdown_pct > 15 ? 'negative' : ''}`}>
            -{snapshot.drawdown_pct.toFixed(2)}%
          </span>
          <span className="metric-change" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-muted)' }}>
            HWM: {formatCurrency(snapshot.high_water_mark)}
          </span>
        </div>

        <div className="metric">
          <span className="metric-label">
            {isInsufficientData ? `${periodLabel} P&L` : pnlLabel}
          </span>
          {isInsufficientData ? (
            <>
              <span className="metric-value" style={{ color: 'var(--text-dim)', fontSize: '0.9rem' }}>—</span>
              <span className="metric-change" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-dim)' }}>
                Insufficient data
              </span>
            </>
          ) : (
            <>
              <span className={`metric-value ${pnlClass}`}>
                {formatPercent(displayPnlPct)}
              </span>
              <span className={`metric-change ${pnlClass}`}>
                {formatCurrency(displayPnl)}
              </span>
            </>
          )}
        </div>

        {isPeriodView && benchmarkReturnPct !== undefined && (
          <div className="metric">
            <span className="metric-label">vs RSP (EW S&P 500)</span>
            <span className={`metric-value ${(displayPnlPct - benchmarkReturnPct) >= 0 ? 'positive' : 'negative'}`}>
              {formatPercent(displayPnlPct - benchmarkReturnPct)}
            </span>
            <span className="metric-change" style={{
              background: (displayPnlPct - benchmarkReturnPct) >= 0 ? 'var(--positive-bg)' : 'var(--negative-bg)',
              color: (displayPnlPct - benchmarkReturnPct) >= 0 ? 'var(--positive)' : 'var(--negative)',
            }}>
              {(displayPnlPct - benchmarkReturnPct) >= 0 ? 'Outperforming' : 'Underperforming'} · RSP {formatPercent(benchmarkReturnPct)}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}
